(() => {
  if (customElements.get('tap-product-sticky')) return;
  const controllers = new WeakMap();
  class TapProductExperience {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.product__info-container');
      this.abort = new AbortController();
      this.resize = new ResizeObserver(() => this.updateDesktopSticky());
      if (this.panel) this.resize.observe(this.panel);
      const header = document.querySelector('.section-header');
      if (header) this.resize.observe(header);
      window.addEventListener('resize', () => this.updateDesktopSticky(), { signal: this.abort.signal });
      root.querySelectorAll('[data-tap-accordion]').forEach(button => this.setAccordion(button, !button.hasAttribute('data-collapsed')));
      root.addEventListener('click', event => {
        const button = event.target.closest('[data-tap-accordion]');
        if (button) this.setAccordion(button, button.getAttribute('aria-expanded') !== 'true');
      }, { signal: this.abort.signal });
      root.querySelectorAll('[dir="auto"] :is(p, li, h2, h3, h4)').forEach(element => {
        if (!element.hasAttribute('dir')) element.setAttribute('dir', 'auto');
      });
      root.addEventListener('shopify:block:select', event => {
        const button = event.target.querySelector('[data-tap-accordion]');
        if (button) this.setAccordion(button, true);
      }, { signal: this.abort.signal });
      this.updateDesktopSticky();
    }
    setAccordion(button, expanded) {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (!content) return;
      button.setAttribute('aria-expanded', String(expanded)); content.hidden = !expanded;
    }
    updateDesktopSticky() {
      if (!this.panel) return;
      const headerHeight = document.querySelector('.section-header')?.getBoundingClientRect().height || 0;
      const fits = this.panel.offsetHeight + headerHeight + 40 < window.innerHeight;
      this.panel.classList.toggle('tap-product-info--sticky', this.root.dataset.tapSticky === 'true' && fits);
    }
    disconnect() { this.abort.abort(); this.resize.disconnect(); }
  }
  if (!customElements.get('tap-product-sticky')) {
    customElements.define('tap-product-sticky', class extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.tap-product');
        this.mainButton = this.root?.querySelector('.product-form__submit');
        if (!this.mainButton) return;
        this.abort = new AbortController();
        this.button = this.querySelector('[data-sticky-submit]');
        this.price = this.querySelector('[data-sticky-price]');
        this.variant = this.querySelector('[data-sticky-variant]');
        this.status = this.querySelector('[data-sticky-status]');
        this.footer = document.querySelector('footer');
        this.button.addEventListener('click', () => {
          // Use the original form, including quantity, properties and any selling plan.
          if (!this.button.disabled) this.mainButton.click();
        }, { signal: this.abort.signal });
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.root.querySelector('.product__info-container'), {
          childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden']
        });
        this.visibilityObserver = new IntersectionObserver(() => this.updateVisibility(), { threshold: [0, 1] });
        this.visibilityObserver.observe(this.mainButton);
        if (this.footer) this.visibilityObserver.observe(this.footer);
        this.overlayObserver = new MutationObserver(() => this.updateVisibility());
        this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        document.querySelectorAll('cart-drawer, cart-notification, .tap-product-modal').forEach(element => {
          this.overlayObserver.observe(element, { attributes: true, subtree: true, attributeFilter: ['open', 'class'] });
        });
        window.addEventListener('scroll', () => this.scheduleVisibility(), { passive: true, signal: this.abort.signal });
        window.addEventListener('resize', () => this.scheduleVisibility(), { passive: true, signal: this.abort.signal });
        document.addEventListener('focusin', () => this.scheduleVisibility(), { signal: this.abort.signal });
        document.addEventListener('focusout', () => this.scheduleVisibility(), { signal: this.abort.signal });
        this.sync();
      }
      sync() {
        const priceBlock = document.getElementById('price-' + this.dataset.section);
        const price = priceBlock?.querySelector('.price');
        const selector = price?.classList.contains('price--on-sale') ? '.price-item--sale' : '.price__regular .price-item--regular:not(s)';
        this.price.textContent = priceBlock?.classList.contains('hidden') ? '' : price?.querySelector(selector)?.textContent.trim() || '';
        this.variant.textContent = [...(this.root.querySelector('variant-selects')?.querySelectorAll('input:checked, select option:checked') || [])].map(input => input.value || input.textContent.trim()).join(' / ');
        this.button.textContent = this.mainButton.querySelector('span')?.textContent.trim() || this.mainButton.textContent.trim();
        this.button.disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.setAttribute('aria-busy', String(this.mainButton.classList.contains('loading')));
        const error = this.root.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
        const message = error?.textContent.trim() || '';
        if (this.status.textContent !== message) {
          this.status.textContent = message;
          if (message && !this.hidden) this.root.querySelector('.product-form__error-message-wrapper')?.scrollIntoView({ block: 'center' });
        }
        this.updateVisibility();
      }
      scheduleVisibility() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateVisibility(); });
      }
      updateVisibility() {
        const pastButton = this.mainButton.getBoundingClientRect().bottom <= 0;
        const atFooter = this.footer && this.footer.getBoundingClientRect().top < window.innerHeight;
        const overlay = document.querySelector('cart-drawer.active, cart-notification .active, cart-notification.active, .tap-product-modal[open], modal-dialog[open]');
        const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
        this.hidden = !window.matchMedia('(max-width: 749px)').matches || !pastButton || !!atFooter || !!overlay || !!editing;
      }
      disconnectedCallback() {
        this.abort?.abort(); this.observer?.disconnect(); this.visibilityObserver?.disconnect(); this.overlayObserver?.disconnect(); cancelAnimationFrame(this.frame);
      }
    });
  }
  const initialize = root => {
    if (!root?.matches('.tap-product') || controllers.has(root)) return;
    controllers.set(root, new TapProductExperience(root));
  };
  document.querySelectorAll('.tap-product').forEach(initialize);
  document.addEventListener('product-info:loaded', event => initialize(event.target));
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.tap-product').forEach(initialize));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.tap-product').forEach(root => {
    controllers.get(root)?.disconnect(); controllers.delete(root);
  }));
})();

